import { formatMailingAddressOneLine } from './mailingAddress';

/**
 * A place as a maps search wants it: the street lines, the locality, the
 * region and the postal code on one line. 🔨 Open in Maps hands over the
 * address, never the site's name; a name is a search and an address is a
 * place (product owner, 2026-09-10).
 */
/**
 * 🔨 The one-line form reads the mailing city and never a municipality
 * (decision 0406). A document that wants the block rather than the line calls
 * `formatMailingAddress`, which keeps the same rule.
 */
export function postalAddressLine(place: { addressLines?: readonly string[] | null; locality?: string | null; administrativeArea?: string | null; postalCode?: string | null } | null | undefined): string | null {
  if (!place) return null;
  // The country is left off a line that names no country of its own, which is what an empty code means here.
  const line = formatMailingAddressOneLine({
    countryCode: '',
    addressLines: place.addressLines ?? [],
    locality: place.locality ?? null,
    administrativeArea: place.administrativeArea ?? null,
    postalCode: place.postalCode ?? null,
  });
  return line === '' ? null : line;
}

/**
 * The one address-to-maps destination in the application: a maps search over
 * the one-line address above, opened outside the application. 🔨 Every Open in
 * Maps builds its link here, the event card's action and a record row's address
 * alike, so a second destination rule cannot drift away from the first. A blank
 * query names no place and answers null, and its caller draws no control at all.
 */
export function mapsSearchHref(query: string | null | undefined): string | null {
  const text = (query ?? '').trim();
  return text === '' ? null : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(text)}`;
}

/** A place as far as its address goes: at least one street line that is not blank (decision 0673). */
export function hasStreetAddress(place: { addressLines?: readonly string[] | null } | null | undefined): boolean {
  return (place?.addressLines ?? []).some((line) => line.trim() !== '');
}

/**
 * 🔒 What a Location is called wherever one line names it (decision 0673): its
 * first street line and its city where it has an address, and its name where it
 * has none — a field, a yard, a tower. 💰 Every label read the first street line
 * alone, so a Location saved with a name and no address read as its id.
 */
export function locationLabel(place: { addressLines?: readonly string[] | null; locality?: string | null; buildingName?: string | null } | null | undefined, fallback = ''): string {
  if (!place) return fallback;
  const first = (place.addressLines ?? []).map((line) => line.trim()).find(Boolean);
  if (first) return [first, place.locality?.trim() || null].filter(Boolean).join(', ');
  return place.buildingName?.trim() || fallback;
}
